<template>
  <VCard class="elevation-12">
    <VCardTitle :class="[headerColor, 'white--text']">
      <VLayout row wrap justify-center>
        <VFlex xs12 text-xs-center>
          <h2>{{ title }}</h2>
          <p class="subtitle mb-0">{{ formattedDate }} {{ formattedTime ? `- ${formattedTime}` : '' }}</p>
        </VFlex>
      </VLayout>
    </VCardTitle>

    <VCardText class="pa-4">
      <!-- Shift Details -->
      <VLayout row wrap class="shift-details">
        <!-- Date -->
        <VFlex xs12 class="shift-item">
          <span class="shift-label">Date:</span>
          <span class="shift-value">{{ formattedDate }}</span>
        </VFlex>

        <!-- Location -->
        <VFlex v-if="shift.locationIdentifier" xs12 class="shift-item">
          <span class="shift-label">Job:</span>
          <span class="shift-value">
            {{ shift.locationIdentifier }}
            <span v-if="shift.locationAddress" class="shift-address">
              - {{ shift.locationAddress }}
            </span>
          </span>
        </VFlex>

        <!-- Start Location -->
        <VFlex v-if="shift.startLocation" xs12 class="shift-item">
          <span class="shift-label">Report to:</span>
          <span class="shift-value">{{ shift.startLocation }}</span>
        </VFlex>

        <!-- Start Time -->
        <VFlex v-if="formattedTime" xs12 class="shift-item">
          <span class="shift-label">Start Time:</span>
          <span class="shift-value">{{ formattedTime }}</span>
        </VFlex>

        <!-- Missing info notice -->
        <VFlex v-if="!shift.startLocation || !formattedTime" xs12 class="mt-3">
          <VAlert :value="true" type="info" outline>
            Please confirm your start location/time with your supervisor.
          </VAlert>
        </VFlex>
      </VLayout>
    </VCardText>
  </VCard>
</template>

<script>
import { VLayout, VFlex } from 'vuetify/lib'
import { VCard, VCardTitle, VCardText } from '@vuetify/VCard'
import VAlert from '@vuetify/VAlert'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

export default {
  name: 'ShiftDisplay',
  components: {
    VLayout,
    VFlex,
    VCard,
    VCardTitle,
    VCardText,
    VAlert,
  },
  props: {
    shift: {
      type: Object,
      required: true,
    },
    dayLabel: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: 'orange darken-1',
    },
  },
  computed: {
    headerColor() {
      return this.color
    },
    title() {
      if (this.dayLabel) {
        return `${this.dayLabel}'s Shift`
      }
      return 'Your Shift'
    },
    formattedDate() {
      if (!this.shift.reportDate) return 'Date not available'
      // Parse MM/DD/YYYY and format with day name
      const parsed = dayjs(this.shift.reportDate, 'MM/DD/YYYY')
      if (!parsed.isValid()) return this.shift.reportDate
      return parsed.format('dddd, MM/DD/YYYY')
    },

    formattedTime() {
      const time = this.shift.startTime
      if (!time || !time.hours) return ''

      const hours = parseInt(time.hours, 10)
      const minutes = parseInt(time.minutes || '0', 10)
      const ampm = (time.am_pm || 'AM').toUpperCase()

      // Convert 12-hour to 24-hour for dayjs
      let hour24 = hours
      if (ampm === 'PM' && hours !== 12) hour24 += 12
      if (ampm === 'AM' && hours === 12) hour24 = 0

      return dayjs().hour(hour24).minute(minutes).format('h:mm A')
    },
  },
}
</script>

<style scoped>
.v-card__title h2 {
  font-weight: 500;
}

.v-card__title .subtitle {
  font-size: 1.1rem;
  opacity: 0.95;
  margin-top: 6px;
}

.shift-details {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
}

.shift-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.shift-item:last-of-type {
  border-bottom: none;
}

.shift-label {
  font-weight: 600;
  margin-right: 8px;
  min-width: 80px;
}

.shift-value {
  flex: 1;
}

.shift-address {
  display: block;
  font-size: 0.9em;
  color: #666;
  margin-top: 4px;
}
</style>
